import React, { useEffect, useState } from "react";
import { Users, Building2, Calendar, Heart, Store as StoreIcon, Mail, LayoutDashboard, MapPin, Clock, Trash2, Plus, Check, X, Euro, Newspaper, Pencil, Church, Eye, EyeOff, Languages, Wand2, Loader2, Upload, Download, FileSpreadsheet, UserPlus, Contact as ContactIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { ImageUpload } from "@/components/ImageUpload";
import api, { formatApiError } from "@/lib/api";
import { PT_DIOCESES } from "@/lib/dioceses";

const ROLES = ["super_admin", "national_admin", "diocesan_admin", "community_coordinator", "store_manager", "member"];
const ROLE_LABELS = {
  super_admin: "Super Admin", national_admin: "Admin Nacional", diocesan_admin: "Admin Diocesano",
  community_coordinator: "Coordenador", store_manager: "Gestor Loja", member: "Membro",
};

function Kpi({ icon: Icon, value, label, gold }) {
  return (
    <div className={`rounded-2xl p-5 border ${gold ? "bg-th-blue text-white border-th-gold/40" : "bg-white border-slate-200"}`}>
      <Icon className={`w-6 h-6 ${gold ? "text-th-gold" : "text-th-blue"}`} />
      <div className={`mt-3 font-display font-bold text-3xl ${gold ? "text-white" : "text-th-blue"}`}>{value}</div>
      <div className={`text-xs uppercase tracking-wide mt-1 ${gold ? "text-white/60" : "text-slate-400"}`}>{label}</div>
    </div>
  );
}

export default function Admin() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { refresh: refreshStore } = useStore();
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [events, setEvents] = useState([]);
  const [intentions, setIntentions] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [news, setNews] = useState([]);
  const [courses, setCourses] = useState([]);
  const [storeVisible, setStoreVisible] = useState(false);

  const canStore = ["super_admin", "store_manager"].includes(user?.role);
  const canCampaign = ["super_admin", "national_admin"].includes(user?.role);
  const canModerate = ["super_admin", "national_admin", "diocesan_admin"].includes(user?.role);

  const loadStats = () => api.get("/admin/stats").then((r) => { setStats(r.data); setStoreVisible(r.data.store_visible); });
  const loadAll = () => {
    loadStats();
    api.get("/admin/members").then((r) => setMembers(r.data));
    api.get("/admin/membership-requests").then((r) => setRequests(r.data));
    api.get("/admin/communities").then((r) => setCommunities(r.data));
    api.get("/admin/events").then((r) => setEvents(r.data));
    api.get("/admin/prayer-intentions").then((r) => setIntentions(r.data));
    api.get("/admin/store/products").then((r) => setProducts(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/campaigns").then((r) => setCampaigns(r.data));
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/news").then((r) => setNews(r.data)).catch(() => {});
    api.get("/admin/courses").then((r) => setCourses(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const toggleStore = async (v) => {
    try { await api.put("/admin/store/settings", { visible: v }); setStoreVisible(v); refreshStore(); toast.success("OK"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const updateRole = async (id, role) => { await api.put(`/admin/members/${id}/role`, { role }); loadAll(); toast.success("OK"); };
  const setRequestStatus = async (id, status) => { await api.put(`/admin/membership-requests/${id}`, { status }); loadAll(); };
  const moderateIntention = async (id, status) => { await api.put(`/admin/prayer-intentions/${id}`, { status }); loadAll(); };
  const delCommunity = async (id) => { await api.delete(`/admin/communities/${id}`); loadAll(); toast.success("OK"); };
  const delEvent = async (id) => { await api.delete(`/admin/events/${id}`); loadAll(); toast.success("OK"); };
  const delProduct = async (id) => { await api.delete(`/admin/store/products/${id}`); loadAll(); toast.success("OK"); };
  const moderateNews = async (id, status) => { await api.put(`/admin/news/${id}`, { status }); loadAll(); };
  const delNews = async (id) => { await api.delete(`/admin/news/${id}`); loadAll(); toast.success("OK"); };
  const delCourse = async (id) => { await api.delete(`/admin/courses/${id}`); loadAll(); toast.success("OK"); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-3 mb-8">
        <LayoutDashboard className="w-7 h-7 text-th-gold" />
        <div>
          <h1 className="font-display font-bold text-3xl text-th-blue">{t("admin_title")}</h1>
          <p className="text-sm text-slate-500">{ROLE_LABELS[user?.role]} · {user?.name}</p>
        </div>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-th-blue/5 p-1">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">{t("admin_dashboard")}</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">{t("admin_requests")}</TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members">{t("admin_members")}</TabsTrigger>
          <TabsTrigger value="communities" data-testid="tab-communities">{t("admin_communities")}</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">{t("admin_events")}</TabsTrigger>
          <TabsTrigger value="intentions" data-testid="tab-intentions">{t("admin_intentions")}</TabsTrigger>
          <TabsTrigger value="news" data-testid="tab-news">{t("nav_news")}</TabsTrigger>
          <TabsTrigger value="school" data-testid="tab-school">{t("nav_school")}</TabsTrigger>
          <TabsTrigger value="store" data-testid="tab-store">{t("admin_store")}</TabsTrigger>
          {canCampaign && <TabsTrigger value="contacts" data-testid="tab-contacts">{t("admin_contacts")}</TabsTrigger>}
          {canCampaign && <TabsTrigger value="campaigns" data-testid="tab-campaigns">{t("admin_campaigns")}</TabsTrigger>}
        </TabsList>

        {/* DASHBOARD */}
        <TabsContent value="dashboard" className="mt-6">
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-kpis">
              <Kpi icon={Users} value={stats.members} label={t("stat_men")} />
              <Kpi icon={Building2} value={stats.communities} label={t("stat_communities")} />
              <Kpi icon={MapPin} value={stats.countries} label={t("stat_countries")} />
              <Kpi icon={Heart} value={stats.pledges.toLocaleString()} label={t("nav_prayer")} gold />
              <Kpi icon={Mail} value={stats.pending_requests} label={t("admin_requests")} />
              <Kpi icon={Calendar} value={stats.upcoming_events} label={t("upcoming_events")} />
              <Kpi icon={Heart} value={stats.pending_intentions} label={t("admin_intentions")} />
              <Kpi icon={Newspaper} value={stats.pending_news ?? 0} label={t("nav_news")} />
              <Kpi icon={Euro} value={`${stats.revenue.toFixed(0)} €`} label="Loja" gold />
            </div>
          )}
          <div className="mt-6 bg-white rounded-2xl p-6 border border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <StoreIcon className="w-6 h-6 text-th-gold" />
              <div>
                <div className="font-display font-semibold text-th-blue">{t("admin_store_toggle")}</div>
                <div className="text-sm text-slate-500">{storeVisible ? "Loja ativa e visível ao público" : "Loja oculta — associação em constituição"}</div>
              </div>
            </div>
            <Switch checked={storeVisible} onCheckedChange={toggleStore} disabled={!canStore} data-testid="store-toggle-switch" />
          </div>
        </TabsContent>

        {/* REQUESTS */}
        <TabsContent value="requests" className="mt-6">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Tipo</TableHead><TableHead>Estado</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id} data-testid={`request-row-${r.id}`}>
                    <TableCell className="font-medium">{r.name}{r.phone && <div className="text-xs text-slate-500 font-normal">{r.phone}</div>}</TableCell>
                    <TableCell>{r.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={r.type === "group_application" ? "border-th-gold text-th-blue" : ""}>{r.type === "join" ? "Adesão" : r.type === "group_application" ? "Candidatura de grupo" : "Nova comunidade"}</Badge>
                      {r.type === "group_application" && (
                        <div className="text-xs text-slate-500 mt-1 max-w-xs" data-testid={`request-group-${r.id}`}>
                          <strong className="text-th-blue">{r.group_name}</strong> · {[r.city, r.diocese || r.country].filter(Boolean).join(", ")}
                          {(r.periodicity || r.prayer_day) && <div>{[r.periodicity, r.prayer_day, r.prayer_time].filter(Boolean).join(" · ")}</div>}
                          {r.message && <div className="italic mt-0.5 line-clamp-2">{r.message}</div>}
                          <div className="text-amber-600 mt-0.5">Comunidade criada (oculta) — tornar visível no separador Comunidades</div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell><Badge className={r.status === "approved" ? "bg-green-600" : r.status === "rejected" ? "bg-red-500" : "bg-amber-500"}>{r.status}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="text-green-600" data-testid={`approve-${r.id}`} onClick={() => setRequestStatus(r.id, "approved")}><Check className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-500" data-testid={`reject-${r.id}`} onClick={() => setRequestStatus(r.id, "rejected")}><X className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
                {requests.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">—</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* MEMBERS */}
        <TabsContent value="members" className="mt-6">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>País</TableHead><TableHead>Perfil</TableHead></TableRow></TableHeader>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell>{m.email}</TableCell>
                    <TableCell>{m.country || "—"}</TableCell>
                    <TableCell>
                      <Select value={m.role} onValueChange={(v) => updateRole(m.id, v)}>
                        <SelectTrigger className="w-40 h-8" data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* COMMUNITIES */}
        <TabsContent value="communities" className="mt-6">
          <CommunityManager communities={communities} onChange={loadAll} onDelete={delCommunity} t={t} />
        </TabsContent>

        {/* EVENTS */}
        <TabsContent value="events" className="mt-6">
          <EventManager events={events} communities={communities} onChange={loadAll} onDelete={delEvent} t={t} />
        </TabsContent>

        {/* INTENTIONS */}
        <TabsContent value="intentions" className="mt-6">
          <div className="grid sm:grid-cols-2 gap-4" data-testid="admin-intentions">
            {intentions.map((it) => (
              <div key={it.id} data-testid={`admin-intention-${it.id}`} className="bg-white rounded-xl p-5 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-th-blue">{it.name}</span>
                  <Badge className={it.status === "approved" ? "bg-green-600" : it.status === "rejected" ? "bg-red-500" : "bg-amber-500"}>{it.status}</Badge>
                </div>
                <p className="text-slate-600 text-sm italic">"{it.text}"</p>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8" data-testid={`intention-approve-${it.id}`} onClick={() => moderateIntention(it.id, "approved")}><Check className="w-3 h-3 mr-1" />Aprovar</Button>
                  <Button size="sm" variant="outline" className="text-red-500 h-8" data-testid={`intention-reject-${it.id}`} onClick={() => moderateIntention(it.id, "rejected")}><X className="w-3 h-3 mr-1" />Rejeitar</Button>
                </div>
              </div>
            ))}
            {intentions.length === 0 && <p className="text-slate-400">—</p>}
          </div>
        </TabsContent>

        {/* NEWS */}
        <TabsContent value="news" className="mt-6">
          <NewsManager news={news} communities={communities} canModerate={canModerate} onModerate={moderateNews} onDelete={delNews} onChange={loadAll} t={t} />
        </TabsContent>

        {/* SCHOOL */}
        <TabsContent value="school" className="mt-6">
          <CourseManager courses={courses} canEdit={canCampaign} onDelete={delCourse} onChange={loadAll} t={t} />
        </TabsContent>

        {/* STORE */}
        <TabsContent value="store" className="mt-6 space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <StoreIcon className="w-6 h-6 text-th-gold" />
              <div><div className="font-display font-semibold text-th-blue">{t("admin_store_toggle")}</div><div className="text-sm text-slate-500">{storeVisible ? "Visível" : "Oculta"}</div></div>
            </div>
            <Switch checked={storeVisible} onCheckedChange={toggleStore} disabled={!canStore} data-testid="store-toggle-switch-2" />
          </div>
          <ProductManager products={products} onChange={loadAll} onDelete={delProduct} canEdit={canStore} t={t} />
          <div>
            <h3 className="font-display font-semibold text-lg text-th-blue mb-3">Encomendas</h3>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Ref</TableHead><TableHead>Cliente</TableHead><TableHead>Total</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                <TableBody>
                  {orders.map((o) => (
                    <TableRow key={o.id}><TableCell>{o.session_id?.slice(0, 12)}</TableCell><TableCell>{o.name || o.email || "—"}</TableCell><TableCell>{(o.amount / 100).toFixed(2)} €</TableCell><TableCell><Badge className={o.payment_status === "paid" ? "bg-green-600" : "bg-amber-500"}>{o.payment_status}</Badge></TableCell></TableRow>
                  ))}
                  {orders.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">—</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* CAMPAIGNS */}
        {canCampaign && (
          <TabsContent value="contacts" className="mt-6">
            <ContactsManager contacts={contacts} onChange={loadAll} t={t} />
          </TabsContent>
        )}

        {canCampaign && (
          <TabsContent value="campaigns" className="mt-6">
            <CampaignManager communities={communities} campaigns={campaigns} onChange={loadAll} t={t} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/* ---------- Community Manager ---------- */
const COMM_FIELDS = [
  { k: "name", label: "Nome" }, { k: "country", label: "País" }, { k: "city", label: "Cidade" },
  { k: "postal_code", label: "Código Postal" }, { k: "diocese", label: "Diocese" },
  { k: "parish", label: "Paróquia" }, { k: "parish_priest", label: "Pároco" },
  { k: "address", label: "Morada" },
  { k: "coordinator_name", label: "Coordenador" },
  { k: "vice_coordinator", label: "Vice-coordenador" }, { k: "secretary", label: "Secretário" },
  { k: "treasurer", label: "Tesoureiro" }, { k: "communications_officer", label: "Responsável de Comunicação" },
  { k: "contact_email", label: "Email" }, { k: "contact_phone", label: "Telefone" },
  { k: "facebook", label: "Facebook (URL)" }, { k: "instagram", label: "Instagram (URL)" },
  { k: "lat", label: "Latitude (mapa)" }, { k: "lng", label: "Longitude (mapa)" },
];
const PERIODICITY_OPTIONS = ["Semanal", "Quinzenal", "Mensal", "Outro"];
const WEEKDAYS = ["Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado", "Domingo"];
const COMM_EMPTY = { name: "", country: "", city: "", postal_code: "", diocese: "", parish: "", parish_priest: "", address: "", periodicity: "", prayer_day: "", prayer_time: "", schedule: "", contact_email: "", contact_phone: "", coordinator_name: "", vice_coordinator: "", secretary: "", treasurer: "", communications_officer: "", facebook: "", instagram: "", description: "", lat: "", lng: "", status: "active", visible: true };

function CommunityManager({ communities, onChange, onDelete, t }) {
  const [form, setForm] = useState(COMM_EMPTY);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [assocOnly, setAssocOnly] = useState(false);

  const openCreate = () => { setForm(COMM_EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({ ...COMM_EMPTY, ...Object.fromEntries(Object.keys(COMM_EMPTY).map((k) => [k, c[k] ?? ""])), visible: c.visible !== false });
    setEditId(c.id); setOpen(true);
  };
  const toggleVisible = async (c) => {
    try {
      await api.put(`/admin/communities/${c.id}/visible`, { visible: c.visible === false });
      onChange();
      toast.success(c.visible === false ? "Comunidade tornada visível" : "Comunidade ocultada");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async () => {
    try {
      const payload = { ...form, lat: form.lat === "" || form.lat == null ? null : parseFloat(form.lat),
        lng: form.lng === "" || form.lng == null ? null : parseFloat(form.lng) };
      if (editId) await api.put(`/admin/communities/${editId}`, payload);
      else await api.post("/admin/communities", payload);
      setOpen(false); setForm(COMM_EMPTY); setEditId(null); onChange(); toast.success("OK");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const associatedCount = communities.filter((c) => c.associated).length;
  const hiddenAssociated = communities.filter((c) => c.associated && c.visible === false).length;
  const shown = assocOnly ? communities.filter((c) => c.associated) : communities;

  // group by country
  const groups = {};
  shown.forEach((c) => { const k = c.country || "—"; (groups[k] = groups[k] || []).push(c); });
  const countries = Object.keys(groups).sort();

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Button onClick={openCreate} className="bg-th-blue text-white" data-testid="add-community-btn"><Plus className="w-4 h-4 mr-1" />{t("admin_add")}</Button>
        {associatedCount > 0 && (
          <button data-testid="filter-associated" onClick={() => setAssocOnly((v) => !v)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${assocOnly ? "bg-th-gold text-th-bluedark border-th-gold" : "bg-white text-th-blue border-slate-200 hover:border-th-gold/60"}`}>
            <Church className="w-4 h-4" />Grupos associados
            <Badge className="bg-th-blue text-white">{associatedCount}</Badge>
            {hiddenAssociated > 0 && <span className="text-xs opacity-70">· {hiddenAssociated} ocultos</span>}
          </button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-th-blue">{editId ? "Editar comunidade" : "Nova comunidade"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            {COMM_FIELDS.map((f) => (
              <div key={f.k} className={f.k === "address" || f.k === "schedule" ? "sm:col-span-2" : ""}>
                <Label className="text-xs">{f.label}</Label>
                <Input data-testid={`comm-${f.k}`} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="mt-1" />
              </div>
            ))}
            <div>
              <Label className="text-xs">Periodicidade</Label>
              <Select value={form.periodicity} onValueChange={(v) => setForm({ ...form, periodicity: v })}>
                <SelectTrigger data-testid="comm-periodicity" className="mt-1"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>
                  {PERIODICITY_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Dia de oração</Label>
                <Select value={form.prayer_day} onValueChange={(v) => setForm({ ...form, prayer_day: v })}>
                  <SelectTrigger data-testid="comm-prayer_day" className="mt-1"><SelectValue placeholder="Dia" /></SelectTrigger>
                  <SelectContent>
                    {WEEKDAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Hora</Label>
                <Input type="time" data-testid="comm-prayer_time" value={form.prayer_time} onChange={(e) => setForm({ ...form, prayer_time: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="sm:col-span-2 flex items-center justify-between rounded-xl border border-slate-200 p-3">
              <div>
                <Label className="text-sm text-th-blue font-medium">Visível no site</Label>
                <div className="text-xs text-slate-500">{form.visible ? "Público — aparece na lista, mapa e contagem" : "Oculto — só visível no backoffice"}</div>
              </div>
              <Switch data-testid="comm-visible" checked={form.visible} onCheckedChange={(v) => setForm({ ...form, visible: v })} />
            </div>
            <div className="sm:col-span-2"><Label className="text-xs">Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-community" className="bg-th-blue text-white">{t("admin_save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-8">
        {countries.map((country) => (
          <div key={country} data-testid={`country-group-${country}`}>
            <div className="flex items-center gap-3 mb-3">
              <h3 className="font-display font-semibold text-lg text-th-blue">{country}</h3>
              <Badge variant="outline" className="text-th-gold border-th-gold/40">{groups[country].length}</Badge>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {groups[country].map((c) => (
                <div key={c.id} className="bg-white rounded-xl p-5 border border-slate-200 border-l-4 border-l-th-blue" data-testid={`admin-community-${c.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="font-medium text-th-blue leading-tight">
                      {c.name}
                      {c.associated && <Badge className="ml-2 bg-th-gold/20 text-th-bluedark border border-th-gold/50 align-middle" data-testid={`assoc-badge-${c.id}`}>Associado</Badge>}
                      {c.visible === false && <Badge className="ml-2 bg-slate-400 text-white align-middle" data-testid={`hidden-badge-${c.id}`}>Oculto</Badge>}
                    </div>
                    <div className="flex shrink-0">
                      <Button size="icon" variant="ghost" className={`h-8 w-8 ${c.visible === false ? "text-slate-400" : "text-green-600"}`} data-testid={`toggle-visible-${c.id}`} title={c.visible === false ? "Tornar visível" : "Ocultar"} onClick={() => toggleVisible(c)}>
                        {c.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button size="icon" variant="ghost" className="text-th-blue h-8 w-8" data-testid={`edit-community-${c.id}`} onClick={() => openEdit(c)}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-red-500 h-8 w-8" data-testid={`del-community-${c.id}`} onClick={() => onDelete(c.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1.5 text-sm text-slate-500">
                    {c.parish && <div className="flex items-start gap-1.5"><Church className="w-3.5 h-3.5 text-th-gold mt-0.5 shrink-0" />{c.parish}</div>}
                    {c.parish_priest && <div className="flex items-start gap-1.5 text-th-blue/80"><span className="text-th-gold text-xs mt-0.5 shrink-0 font-semibold">Pároco:</span>{c.parish_priest}</div>}
                    <div className="flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 text-th-gold mt-0.5 shrink-0" />{[c.address, c.postal_code, c.city].filter(Boolean).join(", ")}</div>
                    {(c.periodicity || c.prayer_day || c.prayer_time || c.schedule) && <div className="flex items-start gap-1.5"><Clock className="w-3.5 h-3.5 text-th-gold mt-0.5 shrink-0" />{[c.periodicity, c.prayer_day, c.prayer_time].filter(Boolean).join(" · ") || c.schedule}</div>}
                    {c.coordinator_name && <div className="flex items-start gap-1.5"><Users className="w-3.5 h-3.5 text-th-gold mt-0.5 shrink-0" />{c.coordinator_name}</div>}
                    {(c.vice_coordinator || c.secretary || c.treasurer || c.communications_officer) && (
                      <div className="pt-1 space-y-0.5 text-xs text-slate-400">
                        {c.vice_coordinator && <div>Vice: {c.vice_coordinator}</div>}
                        {c.secretary && <div>Secretário: {c.secretary}</div>}
                        {c.treasurer && <div>Tesoureiro: {c.treasurer}</div>}
                        {c.communications_officer && <div>Comunicação: {c.communications_officer}</div>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {communities.length === 0 && <p className="text-slate-400">—</p>}
      </div>
    </div>
  );
}

/* ---------- Event Manager ---------- */
const EVENT_EMPTY = { title: "", description: "", date: "", end_date: "", location: "", address: "", city: "", country: "", community_id: "", image_url: "", organizer: "", capacity: "", price: 0, registration_enabled: true };

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

function EventManager({ events, communities, onChange, onDelete, t }) {
  const [form, setForm] = useState(EVENT_EMPTY);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [regList, setRegList] = useState([]);
  const [regEvent, setRegEvent] = useState(null);
  const [regLoading, setRegLoading] = useState(false);
  const [regSearch, setRegSearch] = useState("");

  const openRegs = async (e) => {
    setRegEvent(e); setRegOpen(true); setRegLoading(true); setRegList([]); setRegSearch("");
    try {
      const { data } = await api.get(`/admin/events/${e.id}/registrations`);
      setRegList(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRegLoading(false); }
  };
  const downloadXlsx = async () => {
    try {
      const { data } = await api.get(`/admin/events/${regEvent.id}/registrations.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `inscritos-${(regEvent?.title || "evento").replace(/\s+/g, "-")}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erro ao exportar"); }
  };
  const REG_STATUS = { confirmed: { label: "Confirmado", cls: "bg-green-600" }, pending_payment: { label: "Pagamento pendente", cls: "bg-amber-500" } };
  const confirmedCount = regList.filter((r) => r.status === "confirmed").length;
  const pendingCount = regList.filter((r) => r.status === "pending_payment").length;
  const dioceseCounts = regList.filter((r) => r.status === "confirmed")
    .reduce((acc, r) => { const k = r.diocese || "—"; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const dioceseEntries = Object.entries(dioceseCounts).sort((a, b) => b[1] - a[1]);
  const maxDioceseCount = Math.max(1, ...dioceseEntries.map((e) => e[1]));
  const q = regSearch.trim().toLowerCase();
  const filteredRegs = q
    ? regList.filter((r) => [r.name, r.email, r.diocese, r.parish].some((v) => (v || "").toLowerCase().includes(q)))
    : regList;

  const openCreate = () => { setForm(EVENT_EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (e) => {
    setForm({
      ...EVENT_EMPTY,
      ...Object.fromEntries(Object.keys(EVENT_EMPTY).map((k) => [k, e[k] ?? EVENT_EMPTY[k]])),
      date: toLocalInput(e.date), end_date: toLocalInput(e.end_date), price: e.price ?? 0,
    });
    setEditId(e.id); setOpen(true);
  };
  const save = async () => {
    try {
      const payload = {
        ...form,
        date: form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
        price: parseFloat(form.price) || 0,
        capacity: form.capacity ? parseInt(form.capacity) : null,
      };
      if (!payload.community_id) delete payload.community_id;
      if (editId) await api.put(`/admin/events/${editId}`, payload);
      else await api.post("/admin/events", payload);
      setOpen(false); setForm(EVENT_EMPTY); setEditId(null); onChange(); toast.success("OK");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <Button onClick={openCreate} className="mb-4 bg-th-blue text-white" data-testid="add-event-btn"><Plus className="w-4 h-4 mr-1" />{t("admin_add")}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-th-blue">{editId ? "Editar evento" : "Novo evento"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Título</Label><Input data-testid="event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Data e hora de início</Label><Input type="datetime-local" data-testid="event-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Preço (€) — 0 = gratuito</Label><Input type="number" step="0.01" data-testid="event-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Data e hora de fim (opcional)</Label><Input type="datetime-local" data-testid="event-enddate-input" value={form.end_date} min={form.date || undefined} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Local (nome)</Label><Input data-testid="event-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Morada exata</Label><Input data-testid="event-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Cidade</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">País</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Organização</Label><Input value={form.organizer} onChange={(e) => setForm({ ...form, organizer: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Capacidade</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Imagem</Label><ImageUpload testid="event-image" value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} /></div>
            <div><Label className="text-xs">Comunidade</Label>
              <Select value={form.community_id} onValueChange={(v) => setForm({ ...form, community_id: v })}>
                <SelectTrigger className="mt-1" data-testid="event-community"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{communities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" data-testid="event-reg-enabled" checked={form.registration_enabled} onChange={(e) => setForm({ ...form, registration_enabled: e.target.checked })} />Inscrições abertas</label>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-event" className="bg-th-blue text-white">{t("admin_save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.map((e) => (
          <div key={e.id} className="bg-white rounded-xl p-5 border border-slate-200 border-l-4 border-l-th-blue" data-testid={`admin-event-${e.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <div className="text-xs text-th-gold font-semibold">{new Date(e.date).toLocaleString()}</div>
                <div className="font-medium text-th-blue leading-tight">{e.title}</div>
                <div className="text-sm text-slate-500 truncate">{[e.location, e.address].filter(Boolean).join(" · ")}</div>
                <Badge className={`mt-2 ${e.price > 0 ? "bg-th-gold text-th-bluedark" : "bg-slate-100 text-slate-600"}`}>{e.price > 0 ? `${e.price.toFixed(2)} €` : "Gratuito"}</Badge>
              </div>
              <div className="flex shrink-0">
                <Button size="icon" variant="ghost" className="text-th-blue h-8 w-8" data-testid={`edit-event-${e.id}`} onClick={() => openEdit(e)}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" className="text-red-500 h-8 w-8" data-testid={`del-event-${e.id}`} onClick={() => onDelete(e.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
            <Button variant="outline" size="sm" className="mt-3 w-full border-slate-200 text-th-blue hover:bg-slate-50" data-testid={`view-regs-${e.id}`} onClick={() => openRegs(e)}><Users className="w-3.5 h-3.5 mr-1.5" />Ver inscritos</Button>
          </div>
        ))}
      </div>

      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent className="max-w-5xl w-[95vw] max-h-[88vh] overflow-y-auto" data-testid="registrations-dialog">
          <DialogHeader><DialogTitle className="text-th-blue">Inscritos — {regEvent?.title}</DialogTitle></DialogHeader>
          {regLoading ? (
            <p className="text-slate-500">A carregar…</p>
          ) : (
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="text-sm text-slate-500" data-testid="regs-count">{confirmedCount} confirmado(s){pendingCount ? ` · ${pendingCount} pendente(s)` : ""}</div>
                {regList.length > 0 && (
                  <Input
                    data-testid="regs-search"
                    value={regSearch}
                    onChange={(ev) => setRegSearch(ev.target.value)}
                    placeholder="Pesquisar nome, email, diocese, paróquia…"
                    className="h-9 max-w-xs"
                  />
                )}
                {regList.length > 0 && (
                  <Button size="sm" onClick={downloadXlsx} data-testid="download-regs-xlsx" className="ml-auto bg-th-gold text-th-bluedark hover:bg-th-goldlight font-semibold">
                    <Download className="w-4 h-4 mr-2" />Descarregar Excel
                  </Button>
                )}
              </div>
              {regList.length === 0 ? (
                <p className="text-slate-400 py-6 text-center">Ainda não há inscritos neste evento.</p>
              ) : (
                <>
                  {dioceseEntries.length > 0 && (
                    <div className="mb-5 rounded-xl border border-th-gold/30 bg-th-gold/5 p-4" data-testid="regs-diocese-summary">
                      <div className="text-sm font-semibold text-th-blue mb-3">
                        {confirmedCount} inscrito(s) de {dioceseEntries.length} diocese(s)
                      </div>
                      <div className="space-y-2">
                        {dioceseEntries.map(([dio, n]) => (
                          <div key={dio} className="flex items-center gap-3" data-testid={`diocese-bar-${dio}`}>
                            <div className="w-44 shrink-0 text-xs text-slate-600 truncate" title={dio}>{dio}</div>
                            <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                              <div className="bg-th-blue h-full rounded-full" style={{ width: `${(n / maxDioceseCount) * 100}%` }} />
                            </div>
                            <div className="w-8 text-right text-xs font-semibold text-th-blue">{n}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <Table>
                  <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Diocese</TableHead><TableHead>Paróquia</TableHead><TableHead>Comunicações</TableHead><TableHead>Estado</TableHead><TableHead>Data</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {filteredRegs.map((r) => (
                      <TableRow key={r.id} data-testid={`reg-row-${r.id}`}>
                        <TableCell className="font-medium text-th-blue whitespace-nowrap">{r.name}</TableCell>
                        <TableCell className="whitespace-nowrap">{r.email}</TableCell>
                        <TableCell className="text-slate-600 text-sm">{r.diocese || "—"}</TableCell>
                        <TableCell className="text-slate-600 text-sm">{r.parish || "—"}</TableCell>
                        <TableCell>{r.marketing_consent ? <Badge className="bg-th-gold/20 text-th-bluedark border border-th-gold/50">Sim</Badge> : <span className="text-slate-400 text-sm">—</span>}</TableCell>
                        <TableCell><Badge className={(REG_STATUS[r.status] || {}).cls || "bg-slate-400"}>{(REG_STATUS[r.status] || {}).label || r.status}</Badge></TableCell>
                        <TableCell className="text-slate-500 text-sm whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</TableCell>
                      </TableRow>
                    ))}
                    {filteredRegs.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6">Sem resultados para “{regSearch}”.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
                </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Product Manager ---------- */
function ProductManager({ products, onChange, onDelete, canEdit, t }) {
  const empty = { name: "", description: "", category: "", image_url: "", price: 0, currency: "eur", variants: "", active: true };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const openCreate = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ name: p.name || "", description: p.description || "", category: p.category || "",
      image_url: p.image_url || "", price: p.price ?? 0, currency: p.currency || "eur",
      variants: (p.variants || []).join(", "), active: p.active !== false });
    setEditId(p.id); setOpen(true);
  };
  const save = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, variants: form.variants ? form.variants.split(",").map((s) => s.trim()).filter(Boolean) : [] };
      if (editId) await api.put(`/admin/store/products/${editId}`, payload);
      else await api.post("/admin/store/products", payload);
      setOpen(false); setForm(empty); setEditId(null); onChange(); toast.success("OK");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <h3 className="font-display font-semibold text-lg text-th-blue mb-3">Produtos</h3>
      {canEdit && <Button onClick={openCreate} className="mb-4 bg-th-blue text-white" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1" />{t("admin_add")}</Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-th-blue">{editId ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Nome</Label><Input data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Preço (€)</Label><Input type="number" step="0.01" data-testid="product-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Categoria</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Imagem</Label><ImageUpload testid="product-image" value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} /></div>
            <div><Label className="text-xs">Variantes (separadas por vírgula)</Label><Input data-testid="product-variants" value={form.variants} onChange={(e) => setForm({ ...form, variants: e.target.value })} placeholder="S, M, L" className="mt-1" /></div>
            <div><Label className="text-xs">Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" data-testid="product-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />Produto ativo (visível na loja)</label>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-product" className="bg-th-blue text-white">{t("admin_save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {products.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`admin-product-${p.id}`}>
            <div className="relative">
              {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-28 object-cover" />
                : <div className="w-full h-28 bg-slate-100" />}
              {p.active === false && <span className="absolute top-2 left-2 bg-slate-800/80 text-white text-[10px] px-2 py-0.5 rounded-full">Inativo</span>}
            </div>
            <div className="p-3">
              <div className="font-medium text-th-blue text-sm leading-tight">{p.name}</div>
              <div className="text-xs text-slate-400">{p.category}</div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-th-gold font-semibold">{p.price.toFixed(2)} €</span>
                {canEdit && (
                  <div className="flex">
                    <Button size="icon" variant="ghost" className="text-th-blue h-7 w-7" data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-red-500 h-7 w-7" data-testid={`del-product-${p.id}`} onClick={() => onDelete(p.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Contacts Manager (CRM / mail marketing) ---------- */
function ContactsManager({ contacts, onChange, t }) {
  const empty = { name: "", email: "", phone: "", diocese: "", parish: "", locality: "", postal_code: "", marketing_consent: true };
  const [filters, setFilters] = useState({ search: "", diocese: "", consent: "" });
  const [rows, setRows] = useState(contacts || []);
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assign, setAssign] = useState({ diocese: "", parish: "", only_empty: false });
  const [importing, setImporting] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => { setRows(contacts || []); }, [contacts]);

  const reload = async () => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.diocese) params.diocese = filters.diocese;
    if (filters.consent) params.consent = filters.consent;
    try { const { data } = await api.get("/admin/contacts", { params }); setRows(data); setSelected([]); }
    catch { /* noop */ }
  };
  useEffect(() => { const id = setTimeout(reload, 300); return () => clearTimeout(id); }, [filters]); // eslint-disable-line

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSel = rows.length > 0 && selected.length === rows.length;
  const toggleAll = () => setSelected(allSel ? [] : rows.map((r) => r.id));

  const openCreate = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({ name: c.name || "", email: c.email || "", phone: c.phone || "", diocese: c.diocese || "",
      parish: c.parish || "", locality: c.locality || "", postal_code: c.postal_code || "",
      marketing_consent: c.marketing_consent !== false });
    setEditId(c.id); setOpen(true);
  };
  const save = async () => {
    if (!form.email) { toast.error("O email é obrigatório."); return; }
    try {
      if (editId) await api.put(`/admin/contacts/${editId}`, form);
      else await api.post("/admin/contacts", form);
      toast.success("Contacto guardado."); setOpen(false); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Eliminar este contacto?")) return; try { await api.delete(`/admin/contacts/${id}`); onChange(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const doAssign = async () => {
    if (!assign.diocese && !assign.parish) { toast.error("Indique diocese e/ou paróquia."); return; }
    try {
      const { data } = await api.post("/admin/contacts/bulk-assign", { ids: selected, ...assign });
      toast.success(`${data.modified} contacto(s) atribuído(s).`); setAssignOpen(false); setAssign({ diocese: "", parish: "", only_empty: false }); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const download = async (path, filename) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a");
      a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao descarregar."); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importados ${data.created} · atualizados ${data.updated} · ignorados ${data.skipped}`);
      if (data.errors?.length) toast.message("Algumas linhas não foram importadas", { description: data.errors.slice(0, 5).join(" · ") });
      onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erro na importação"); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onImport} data-testid="contacts-import-input" />
        <Button size="sm" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="contacts-import-btn" className="bg-th-blue text-white hover:bg-th-bluedark">
          {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Importar Excel
        </Button>
        <Button size="sm" variant="outline" onClick={() => download("/admin/contacts/template.xlsx", "modelo-contactos.xlsx")} data-testid="contacts-template-btn" className="border-slate-300 text-th-blue"><FileSpreadsheet className="w-4 h-4 mr-2" />Modelo</Button>
        <Button size="sm" variant="outline" onClick={() => download("/admin/contacts.xlsx", "contactos.xlsx")} data-testid="contacts-export-btn" className="border-slate-300 text-th-blue"><Download className="w-4 h-4 mr-2" />Exportar</Button>
        <Button size="sm" onClick={openCreate} data-testid="contact-add-btn" className="bg-th-gold text-th-bluedark hover:bg-th-goldlight"><UserPlus className="w-4 h-4 mr-2" />Novo contacto</Button>
        {selected.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => setAssignOpen(true)} data-testid="contacts-assign-btn" className="border-th-gold text-th-blue"><Church className="w-4 h-4 mr-2" />Atribuir paróquia ({selected.length})</Button>
        )}
        <span className="ml-auto text-sm text-slate-500" data-testid="contacts-count">{rows.length} contacto(s)</span>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input placeholder="Pesquisar nome, email, localidade…" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="contacts-search" className="max-w-xs" />
        <Select value={filters.diocese || "all"} onValueChange={(v) => setFilters({ ...filters, diocese: v === "all" ? "" : v })}>
          <SelectTrigger className="w-56" data-testid="contacts-filter-diocese"><SelectValue placeholder="Diocese" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todas as dioceses</SelectItem>{PT_DIOCESES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.consent || "all"} onValueChange={(v) => setFilters({ ...filters, consent: v === "all" ? "" : v })}>
          <SelectTrigger className="w-48" data-testid="contacts-filter-consent"><SelectValue placeholder="Consentimento" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="yes">Com consentimento</SelectItem><SelectItem value="no">Sem consentimento</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"><input type="checkbox" checked={allSel} onChange={toggleAll} data-testid="contacts-select-all" /></TableHead>
              <TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Telefone</TableHead>
              <TableHead>Diocese</TableHead><TableHead>Paróquia</TableHead><TableHead>Localidade</TableHead>
              <TableHead className="text-center">RGPD</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                <TableCell><input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSel(c.id)} data-testid={`contact-sel-${c.id}`} /></TableCell>
                <TableCell className="font-medium text-th-blue">{c.name || "—"}</TableCell>
                <TableCell className="text-slate-600">{c.email}</TableCell>
                <TableCell className="text-slate-600">{c.phone || "—"}</TableCell>
                <TableCell className="text-slate-600 text-xs">{c.diocese || "—"}</TableCell>
                <TableCell className="text-slate-600 text-xs">{c.parish || "—"}</TableCell>
                <TableCell className="text-slate-600 text-xs">{c.locality || "—"}</TableCell>
                <TableCell className="text-center">{c.marketing_consent ? <Check className="w-4 h-4 text-green-600 inline" /> : <span className="text-slate-300">—</span>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`contact-edit-${c.id}`}><Pencil className="w-4 h-4 text-th-blue" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(c.id)} data-testid={`contact-del-${c.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Sem contactos. Importe um Excel ou adicione manualmente.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="contact-dialog">
          <DialogHeader><DialogTitle>{editId ? "Editar contacto" : "Novo contacto"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label className="text-xs">Nome</Label><Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Email *</Label><Input data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Telefone</Label><Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Diocese</Label>
              <Select value={form.diocese || "none"} onValueChange={(v) => setForm({ ...form, diocese: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1" data-testid="contact-diocese"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{PT_DIOCESES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Paróquia</Label><Input data-testid="contact-parish" value={form.parish} onChange={(e) => setForm({ ...form, parish: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Localidade</Label><Input data-testid="contact-locality" value={form.locality} onChange={(e) => setForm({ ...form, locality: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Código Postal</Label><Input data-testid="contact-postal" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="mt-1" /></div>
            <div className="sm:col-span-2 flex items-center gap-2 mt-1">
              <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} data-testid="contact-consent" />
              <Label className="text-xs">Consentimento para comunicações (RGPD)</Label>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="contact-save" className="bg-th-gold text-th-bluedark hover:bg-th-goldlight">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle>Atribuir a {selected.length} contacto(s)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Diocese</Label>
              <Select value={assign.diocese || "none"} onValueChange={(v) => setAssign({ ...assign, diocese: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1" data-testid="assign-diocese"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— (não alterar)</SelectItem>{PT_DIOCESES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Paróquia</Label><Input data-testid="assign-parish" value={assign.parish} onChange={(e) => setAssign({ ...assign, parish: e.target.value })} placeholder="(não alterar se vazio)" className="mt-1" /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={assign.only_empty} onChange={(e) => setAssign({ ...assign, only_empty: e.target.checked })} data-testid="assign-only-empty" />
              <Label className="text-xs">Apenas preencher onde ainda está vazio</Label>
            </div>
            <p className="text-xs text-slate-400">Útil para atribuir por área de residência: filtre por localidade na pesquisa, selecione os contactos e defina a paróquia/diocese.</p>
          </div>
          <DialogFooter><Button onClick={doAssign} data-testid="assign-save" className="bg-th-gold text-th-bluedark hover:bg-th-goldlight">Atribuir</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ---------- Campaign Manager ---------- */
function CampaignManager({ communities, campaigns, onChange, t }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ subject: "", message: "", segment_community: "", segment_language: "", segment_diocese: "", segment_parish: "", audience: "members" });
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState(user?.email || "");
  const [counts, setCounts] = useState({ members: 0, event_contacts: 0, contacts: 0, registrations: 0 });
  useEffect(() => { api.get("/admin/audience-counts").then((r) => setCounts(r.data)).catch(() => {}); }, []);
  const sendTest = async () => {
    if (!form.subject || !form.message) { toast.error("Preencha o assunto e a mensagem primeiro."); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/campaigns/test", { subject: form.subject, message: form.message, to: testEmail || undefined });
      toast.success(`Email de teste enviado para ${data.to}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };
  const send = async () => {
    setSending(true);
    try {
      const payload = { ...form };
      if (!payload.segment_community) delete payload.segment_community;
      if (!payload.segment_language) delete payload.segment_language;
      if (!payload.segment_diocese) delete payload.segment_diocese;
      if (!payload.segment_parish) delete payload.segment_parish;
      const { data } = await api.post("/admin/campaigns", payload);
      toast.success(`Enviado a ${data.recipients_count} destinatário(s)`);
      setForm({ subject: "", message: "", segment_community: "", segment_language: "", segment_diocese: "", segment_parish: "", audience: "members" });
      onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const isMembers = form.audience === "members" || form.audience === "all";
  const isContacts = form.audience === "event_contacts" || form.audience === "all";
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="bg-white rounded-2xl p-6 border border-slate-200 space-y-4">
        <h3 className="font-display font-semibold text-lg text-th-blue">Nova campanha</h3>
        <div><Label className="text-xs">Assunto</Label><Input data-testid="campaign-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1" /></div>
        <div><Label className="text-xs">Mensagem</Label><Textarea data-testid="campaign-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="mt-1" /></div>
        <div><Label className="text-xs">Destinatários</Label>
          <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
            <SelectTrigger className="mt-1" data-testid="campaign-audience"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="members">Membros ({counts.members})</SelectItem>
              <SelectItem value="event_contacts">Contactos e inscritos c/ consentimento ({counts.event_contacts})</SelectItem>
              <SelectItem value="all">Todos (membros + contactos c/ consentimento)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-400 mt-1">Contactos (importados) e inscritos em eventos só recebem se tiverem dado consentimento (RGPD).</p>
        </div>
        <div className={`grid grid-cols-2 gap-3 ${isMembers ? "" : "opacity-40 pointer-events-none"}`}>
          <div><Label className="text-xs">Comunidade (membros)</Label>
            <Select value={form.segment_community || "all"} onValueChange={(v) => setForm({ ...form, segment_community: v === "all" ? "" : v })}>
              <SelectTrigger className="mt-1" data-testid="campaign-community"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas</SelectItem>{communities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Idioma (membros)</Label>
            <Select value={form.segment_language || "all"} onValueChange={(v) => setForm({ ...form, segment_language: v === "all" ? "" : v })}>
              <SelectTrigger className="mt-1" data-testid="campaign-language"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="pt">PT</SelectItem><SelectItem value="es">ES</SelectItem><SelectItem value="en">EN</SelectItem><SelectItem value="fr">FR</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className={`grid grid-cols-2 gap-3 ${isContacts ? "" : "opacity-40 pointer-events-none"}`}>
          <div><Label className="text-xs">Diocese (contactos)</Label>
            <Select value={form.segment_diocese || "all"} onValueChange={(v) => setForm({ ...form, segment_diocese: v === "all" ? "" : v })}>
              <SelectTrigger className="mt-1" data-testid="campaign-diocese"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas</SelectItem>{PT_DIOCESES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Paróquia (contactos)</Label>
            <Input data-testid="campaign-parish" value={form.segment_parish} onChange={(e) => setForm({ ...form, segment_parish: e.target.value })} placeholder="Todas" className="mt-1" />
          </div>
        </div>
        <Button onClick={send} disabled={sending || !form.subject || !form.message} data-testid="campaign-send" className="w-full bg-th-gold text-th-bluedark hover:bg-th-goldlight"><Mail className="w-4 h-4 mr-2" />{sending ? t("submitting") : "Enviar"}</Button>
      </div>
      <div>
        <h3 className="font-display font-semibold text-lg text-th-blue mb-3">Pré-visualização</h3>
        <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm" data-testid="campaign-preview">
          <div className="bg-th-blue px-6 py-5">
            <div className="text-th-gold text-[11px] tracking-[2px] uppercase">Terço dos Homens · Europa</div>
            <div className="text-white font-display font-bold text-xl mt-1" data-testid="campaign-preview-subject">{form.subject || "Assunto da campanha"}</div>
          </div>
          <div className="bg-white px-6 py-5 text-sm text-slate-600 leading-relaxed min-h-[120px] whitespace-pre-wrap" data-testid="campaign-preview-body">
            {form.message || "O corpo da mensagem aparece aqui à medida que escreve. As quebras de linha são mantidas."}
            <div className="mt-6 pt-3 border-t border-slate-200 text-[11px] text-slate-400">
              Enviado por Terço dos Homens. Nunca lhe pediremos a sua palavra-passe ou dados de cartão por email.
            </div>
          </div>
        </div>
        <div className="mt-4 bg-white rounded-xl p-4 border border-slate-200 space-y-2">
          <Label className="text-xs">Enviar teste para</Label>
          <div className="flex gap-2">
            <Input data-testid="campaign-test-email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder={user?.email || "o.seu@email.pt"} className="flex-1" />
            <Button onClick={sendTest} disabled={testing || !form.subject || !form.message} data-testid="campaign-test-send" variant="outline" className="border-th-gold text-th-blue whitespace-nowrap">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}Enviar teste
            </Button>
          </div>
          <p className="text-xs text-slate-400">Recebe primeiro uma cópia (assunto marcado com [TESTE]) para rever antes do envio em massa.</p>
        </div>

        <h3 className="font-display font-semibold text-lg text-th-blue mb-3 mt-6">Histórico</h3>
        <div className="space-y-3">
          {campaigns.map((c) => (
            <div key={c.id} className="bg-white rounded-xl p-4 border border-slate-200" data-testid={`campaign-row-${c.id}`}>
              <div className="font-medium text-th-blue">{c.subject}</div>
              <div className="text-xs text-slate-500">{new Date(c.sent_at).toLocaleString()} · {c.recipients_count} destinatários</div>
            </div>
          ))}
          {campaigns.length === 0 && <p className="text-slate-400 text-sm">—</p>}
        </div>
      </div>
    </div>
  );
}


/* ---------- News Manager ---------- */
const NEWS_LANGS = [["es", "Español"], ["en", "English"], ["fr", "Français"], ["it", "Italiano"], ["pl", "Polski"], ["de", "Deutsch"], ["uk", "Українська"], ["ru", "Русский"]];

function NewsManager({ news, communities, canModerate, onModerate, onDelete, onChange, t }) {
  const empty = { title: "", body: "", image_url: "", community_id: "", translations: {} };
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [trLang, setTrLang] = useState("en");
  const [translating, setTranslating] = useState(false);

  const translateAI = async () => {
    if (!form.title || !form.body) { toast.error("Preencha o título e o texto em português primeiro."); return; }
    setTranslating(true);
    try {
      const { data } = await api.post("/news/translate", { title: form.title, body: form.body });
      setForm((f) => ({ ...f, translations: { ...(f.translations || {}), ...data.translations } }));
      toast.success("Traduções geradas nos 8 idiomas.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTranslating(false); }
  };

  const openCreate = () => { setForm(empty); setEditId(null); setTrLang("en"); setOpen(true); };
  const openEdit = (n) => {
    setForm({ title: n.title || "", body: n.body || "", image_url: n.image_url || "", community_id: n.community_id || "", translations: n.translations || {} });
    setEditId(n.id); setTrLang("en"); setOpen(true);
  };
  const trVal = (field) => (form.translations?.[trLang]?.[field]) || "";
  const setTr = (field, val) => setForm((f) => ({ ...f, translations: { ...(f.translations || {}), [trLang]: { ...((f.translations || {})[trLang] || {}), [field]: val } } }));

  const save = async () => {
    try {
      const payload = { ...form };
      if (!payload.community_id) delete payload.community_id;
      const tr = {};
      Object.entries(payload.translations || {}).forEach(([k, v]) => { if (v && (v.title || v.body)) tr[k] = v; });
      payload.translations = tr;
      if (editId) await api.put(`/admin/news/${editId}/edit`, payload);
      else await api.post("/news", payload);
      setOpen(false); setForm(empty); setEditId(null); onChange();
      toast.success(editId ? "Notícia atualizada" : t("news_pending_note"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const badge = (s) => s === "approved" ? "bg-green-600" : s === "rejected" ? "bg-red-500" : "bg-amber-500";
  const trCount = (n) => Object.keys(n.translations || {}).length;

  return (
    <div>
      <Button onClick={openCreate} className="mb-4 bg-th-blue text-white" data-testid="add-news-btn"><Plus className="w-4 h-4 mr-1" />{t("news_submit")}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-th-blue">{editId ? "Editar notícia" : t("news_submit")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Título (Português)</Label><Input data-testid="news-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Texto (Português)</Label><Textarea data-testid="news-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} className="mt-1" /></div>
            <div><Label className="text-xs">Imagem</Label><ImageUpload testid="news-image" value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} /></div>
            <div><Label className="text-xs">Comunidade</Label>
              <Select value={form.community_id} onValueChange={(v) => setForm({ ...form, community_id: v })}>
                <SelectTrigger className="mt-1" data-testid="news-community"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{communities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="rounded-xl border border-th-gold/40 bg-th-gold/5 p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <Languages className="w-4 h-4 text-th-gold" />
                  <span className="text-sm font-medium text-th-blue">Traduções (opcional)</span>
                </div>
                <Button type="button" size="sm" variant="outline" data-testid="news-translate-ai" onClick={translateAI} disabled={translating || !form.title || !form.body}
                  className="border-th-gold text-th-bluedark hover:bg-th-gold/10 h-8">
                  {translating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                  {translating ? "A traduzir..." : "Traduzir com IA"}
                </Button>
              </div>
              <Select value={trLang} onValueChange={setTrLang}>
                <SelectTrigger className="mb-2" data-testid="news-tr-lang"><SelectValue /></SelectTrigger>
                <SelectContent>{NEWS_LANGS.map(([code, name]) => (
                  <SelectItem key={code} value={code}>{name}{form.translations?.[code]?.title || form.translations?.[code]?.body ? " ✓" : ""}</SelectItem>
                ))}</SelectContent>
              </Select>
              <Input data-testid="news-tr-title" value={trVal("title")} onChange={(e) => setTr("title", e.target.value)} placeholder="Título traduzido" className="mb-2" />
              <Textarea data-testid="news-tr-body" value={trVal("body")} onChange={(e) => setTr("body", e.target.value)} rows={4} placeholder="Texto traduzido" />
              <p className="text-xs text-slate-400 mt-1">O site mostra a versão do idioma do visitante; se faltar, usa o Português.</p>
            </div>
            {!editId && <p className="text-xs text-slate-400">{t("news_pending_note")}</p>}
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-news" disabled={!form.title || !form.body} className="bg-th-blue text-white">{t("admin_save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {news.map((n) => (
          <div key={n.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`admin-news-${n.id}`}>
            {n.image_url && <img src={n.image_url} alt={n.title} className="w-full h-28 object-cover" />}
            <div className="p-4">
              <div className="flex items-center justify-between mb-1"><Badge className={badge(n.status)}>{n.status}</Badge>
                <div className="flex">
                  {trCount(n) > 0 && <Badge className="bg-th-gold/20 text-th-bluedark border border-th-gold/50 mr-1" data-testid={`news-tr-count-${n.id}`}><Languages className="w-3 h-3 mr-1" />{trCount(n)}</Badge>}
                  <Button size="icon" variant="ghost" className="text-th-blue h-7 w-7" data-testid={`edit-news-${n.id}`} onClick={() => openEdit(n)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-red-500 h-7 w-7" data-testid={`del-news-${n.id}`} onClick={() => onDelete(n.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="font-medium text-th-blue text-sm leading-tight">{n.title}</div>
              <div className="text-xs text-slate-400 mt-1">{n.author_name}</div>
              {canModerate && n.status !== "approved" && (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8" data-testid={`news-approve-${n.id}`} onClick={() => onModerate(n.id, "approved")}><Check className="w-3 h-3 mr-1" />Aprovar</Button>
                  <Button size="sm" variant="outline" className="text-red-500 h-8" data-testid={`news-reject-${n.id}`} onClick={() => onModerate(n.id, "rejected")}><X className="w-3 h-3" /></Button>
                </div>
              )}
            </div>
          </div>
        ))}
        {news.length === 0 && <p className="text-slate-400">—</p>}
      </div>
    </div>
  );
}

/* ---------- Course Manager ---------- */
function CourseManager({ courses, canEdit, onDelete, onChange, t }) {
  const empty = { title: "", description: "", level: "", access_level: "membros", image_url: "", order: 0, modules: [] };
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const LVL = { membros: "Membros", coordenadores: "Coordenadores", direcao: "Direção" };

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({
      title: c.title || "", description: c.description || "", level: c.level || "",
      access_level: c.access_level || "membros", image_url: c.image_url || "", order: c.order || 0,
      modules: (c.modules || []).map((m) => ({ id: m.id, title: m.title || "", duration: m.duration || "", content: m.content || "",
        quiz: m.quiz ? { question: m.quiz.question || "", options: [...(m.quiz.options || ["", "", ""]), "", "", ""].slice(0, 3), answer_index: m.quiz.answer_index || 0 } : null })),
    });
    setEditId(c.id); setOpen(true);
  };
  const setModule = (i, patch) => setForm((f) => ({ ...f, modules: f.modules.map((m, idx) => idx === i ? { ...m, ...patch } : m) }));
  const addModule = () => setForm((f) => ({ ...f, modules: [...f.modules, { id: `m${f.modules.length + 1}`, title: "", duration: "", content: "", quiz: null }] }));
  const removeModule = (i) => setForm((f) => ({ ...f, modules: f.modules.filter((_, idx) => idx !== i) }));
  const toggleQuiz = (i) => setModule(i, { quiz: form.modules[i].quiz ? null : { question: "", options: ["", "", ""], answer_index: 0 } });
  const setOption = (i, oi, val) => setModule(i, { quiz: { ...form.modules[i].quiz, options: form.modules[i].quiz.options.map((o, k) => k === oi ? val : o) } });

  const save = async () => {
    try {
      const modules = form.modules.filter((m) => m.title.trim()).map((m, i) => ({
        id: m.id || `m${i + 1}`, title: m.title.trim(), duration: m.duration || "", content: m.content || "",
        quiz: m.quiz && m.quiz.question.trim() ? { question: m.quiz.question.trim(), options: m.quiz.options.map((o) => o.trim()).filter(Boolean), answer_index: m.quiz.answer_index } : null,
      }));
      const payload = { ...form, order: Number(form.order) || 0, modules, published: true };
      if (editId) await api.put(`/admin/courses/${editId}`, payload);
      else await api.post("/admin/courses", payload);
      setOpen(false); setForm(empty); setEditId(null); onChange(); toast.success("OK");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      {canEdit && (
        <>
          <Button className="mb-4 bg-th-blue text-white" data-testid="add-course-btn" onClick={openNew}><Plus className="w-4 h-4 mr-1" />{t("admin_add")}</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
              <DialogHeader><DialogTitle className="text-th-blue">{editId ? "Editar curso" : "Novo curso"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Título</Label><Input data-testid="course-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs">Nível (etiqueta)</Label><Input data-testid="course-level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="mt-1" /></div>
                  <div>
                    <Label className="text-xs">Nível de acesso</Label>
                    <Select value={form.access_level} onValueChange={(v) => setForm({ ...form, access_level: v })}>
                      <SelectTrigger data-testid="course-access-level" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="membros">Membros</SelectItem>
                        <SelectItem value="coordenadores">Coordenadores</SelectItem>
                        <SelectItem value="direcao">Direção</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Ordem</Label><Input type="number" data-testid="course-order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1" /></div>
                </div>
                <div><Label className="text-xs">Imagem</Label><ImageUpload testid="course-image" value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} /></div>
                <div><Label className="text-xs">Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>

                <div className="flex items-center justify-between pt-1">
                  <Label className="text-sm text-th-blue font-medium">Módulos ({form.modules.length})</Label>
                  <Button size="sm" variant="outline" className="border-slate-300 text-th-blue" data-testid="add-module-btn" onClick={addModule}><Plus className="w-3.5 h-3.5 mr-1" />Módulo</Button>
                </div>
                <div className="space-y-4">
                  {form.modules.map((m, i) => (
                    <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`module-editor-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 font-medium w-6">{i + 1}.</span>
                        <Input placeholder="Título do módulo" data-testid={`module-title-${i}`} value={m.title} onChange={(e) => setModule(i, { title: e.target.value })} className="flex-1" />
                        <Input placeholder="Duração" value={m.duration} onChange={(e) => setModule(i, { duration: e.target.value })} className="w-24" />
                        <Button size="icon" variant="ghost" className="text-red-500 h-8 w-8" data-testid={`remove-module-${i}`} onClick={() => removeModule(i)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                      <Textarea placeholder="Conteúdo do módulo" data-testid={`module-content-${i}`} value={m.content} onChange={(e) => setModule(i, { content: e.target.value })} rows={3} />
                      <div className="flex items-center gap-2 pt-1">
                        <Switch checked={!!m.quiz} onCheckedChange={() => toggleQuiz(i)} data-testid={`module-quiz-toggle-${i}`} />
                        <Label className="text-xs text-slate-500">Pergunta de verificação (quiz)</Label>
                      </div>
                      {m.quiz && (
                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid={`module-quiz-${i}`}>
                          <Input placeholder="Pergunta" data-testid={`quiz-question-${i}`} value={m.quiz.question} onChange={(e) => setModule(i, { quiz: { ...m.quiz, question: e.target.value } })} />
                          {m.quiz.options.map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-2">
                              <input type="radio" name={`ans-${i}`} checked={m.quiz.answer_index === oi} onChange={() => setModule(i, { quiz: { ...m.quiz, answer_index: oi } })} data-testid={`quiz-answer-${i}-${oi}`} className="accent-th-blue" />
                              <Input placeholder={`Opção ${oi + 1}`} value={opt} onChange={(e) => setOption(i, oi, e.target.value)} data-testid={`quiz-option-${i}-${oi}`} className="flex-1" />
                            </div>
                          ))}
                          <p className="text-xs text-slate-400">Selecione o círculo da opção correta.</p>
                        </div>
                      )}
                    </div>
                  ))}
                  {form.modules.length === 0 && <p className="text-slate-400 text-sm">Ainda sem módulos.</p>}
                </div>
              </div>
              <DialogFooter><Button onClick={save} data-testid="save-course" disabled={!form.title} className="bg-th-blue text-white">{t("admin_save")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {courses.map((c) => (
          <div key={c.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`admin-course-${c.id}`}>
            <img src={c.image_url} alt={c.title} className="w-full h-24 object-cover" />
            <div className="p-4">
              <div className="flex items-center justify-between"><div className="flex gap-1.5 flex-wrap items-center"><Badge className="bg-slate-100 text-slate-500">#{c.order ?? 0}</Badge>{c.level && c.level !== LVL[c.access_level] && <Badge className="bg-th-gold text-th-bluedark">{c.level}</Badge>}{c.access_level && <Badge variant="outline" className="border-th-blue/30 text-th-blue">{LVL[c.access_level]}</Badge>}</div>
                {canEdit && <div className="flex shrink-0"><Button size="icon" variant="ghost" className="text-th-blue h-7 w-7" data-testid={`edit-course-${c.id}`} onClick={() => openEdit(c)}><Pencil className="w-4 h-4" /></Button><Button size="icon" variant="ghost" className="text-red-500 h-7 w-7" data-testid={`del-course-${c.id}`} onClick={() => { if (window.confirm(`Eliminar o curso "${c.title}"?`)) onDelete(c.id); }}><Trash2 className="w-4 h-4" /></Button></div>}
              </div>
              <div className="font-medium text-th-blue text-sm mt-1">{c.title}</div>
              <div className="text-xs text-slate-400 mt-1">{c.modules?.length || 0} {t("school_modules")}</div>
            </div>
          </div>
        ))}
        {courses.length === 0 && <p className="text-slate-400">—</p>}
      </div>
    </div>
  );
}
